import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import * as referenceApi from '@platform/core/src/services/referenceApi';

const ReferenceContext = createContext(null);
/**
 * Provides a centralized way to manage resource references (parent-child relationships).
 */
export const ReferenceProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  /**
   * A function to add or remove reference links in bulk.
   * @param {Array<import('../services/referenceApi').ReferenceAction>} actions - An array of actions to perform.
   * @returns {Promise<import('../services/referenceApi').ManageReferenceResult[] | null>} The array of results, or null if an error occurred.
   */
  const manageReferences = useCallback(async (actions) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await referenceApi.manageReferences(actions);
      return response.results;
    } catch (err) {
      setError(err);
      console.error("Failed to manage references:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  const value = useMemo(() => ({
    isLoading,
    error,
    manageReferences,
  }), [isLoading, error, manageReferences]);
  return (
    <ReferenceContext.Provider value={value}>
      {children}
    </ReferenceContext.Provider>
  );
};
/**
 * Custom hook to access the reference management context.
 * @returns {{
 *   isLoading: boolean,
 *   error: Error | null,
 *   manageReferences: (actions: import('../services/referenceApi').ReferenceAction[]) => Promise<import('../services/referenceApi').ManageReferenceResult[] | null>
 * }}
 */
export const useReferences = () => {
  const context = useContext(ReferenceContext);
  if (context === null) {
    throw new Error('useReferences must be used within a ReferenceProvider');
  }
  return context;
};